/**
 * Modal com lançamentos de uma categoria do DFC.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowUpRight, Loader2, Receipt } from 'lucide-react'
import Modal from '../../../../components/ERP/Modal/Modal'
import { dfcService } from '../../../../services/dfcService'
import { formatDateOnly } from '../../../../utils/dateUtils'
import { getDfcTransactionNavigation } from '../../../../utils/dfcTransactionNavigation'
import './DFCCategoryTransactionsModal.css'

function DFCCategoryTransactionsModal({
  isOpen,
  onClose,
  category,
  flowType,
  month,
  year,
  accountId = null
}) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!isOpen || !category) return undefined

    let cancelled = false

    const loadTransactions = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await dfcService.getCategoryTransactions({
          month,
          year,
          categoryId: category.categoryId,
          flowType,
          accountId
        })

        if (!cancelled) {
          if (response.success) {
            setData(response.data)
          } else {
            setError('Erro ao carregar lançamentos da categoria')
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao carregar lançamentos DFC:', err)
          setError('Erro ao carregar lançamentos. Tente novamente.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadTransactions()

    return () => {
      cancelled = true
    }
  }, [isOpen, category, flowType, month, year, accountId])

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const handleOpenTransaction = (transaction) => {
    const navigation = getDfcTransactionNavigation(transaction)
    if (!navigation) return

    onClose()
    navigate(navigation.path, { state: navigation.state })
  }

  const flowLabel = flowType === 'inflow' ? 'Entrada' : 'Saída'
  const transactions = data?.transactions || []

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category?.categoryName || 'Categoria'}
      subtitle={`${flowLabel} de caixa · ${category?.transactionCount || 0} lançamento(s)`}
      icon={<Receipt size={22} />}
      size="large"
    >
      <div className="dfc-category-modal">
        <div className="dfc-category-modal-summary">
          <div>
            <span className="dfc-category-modal-summary-label">Total da categoria</span>
            <strong>{formatCurrency(data?.totalAmount ?? category?.totalAmount ?? 0)}</strong>
          </div>
          <div>
            <span className="dfc-category-modal-summary-label">Período</span>
            <strong>{String(month).padStart(2, '0')}/{year}</strong>
          </div>
        </div>

        {loading && (
          <div className="dfc-category-modal-loading">
            <Loader2 size={24} className="dfc-category-modal-spinner" />
            <span>Carregando lançamentos...</span>
          </div>
        )}

        {error && (
          <div className="dfc-category-modal-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && transactions.length === 0 && (
          <div className="dfc-category-modal-empty">
            <p>Nenhum lançamento encontrado para esta categoria.</p>
          </div>
        )}

        {!loading && !error && transactions.length > 0 && (
          <div className="dfc-category-modal-list">
            {transactions.map((transaction) => {
              const navigation = getDfcTransactionNavigation(transaction)
              const rowKey = `${transaction.source || 'bank_statement'}-${transaction.id}`

              return (
                <button
                  key={rowKey}
                  type="button"
                  className="dfc-category-modal-item"
                  onClick={() => handleOpenTransaction(transaction)}
                  disabled={!navigation}
                  title={navigation ? 'Abrir lançamento' : 'Lançamento sem destino disponível'}
                >
                  <div className="dfc-category-modal-item-main">
                    <span className="dfc-category-modal-item-date">
                      {formatDateOnly(transaction.date)}
                    </span>
                    <span className="dfc-category-modal-item-description">
                      {transaction.description || 'Sem descrição'}
                    </span>
                    <span className="dfc-category-modal-item-account">
                      {transaction.accountName || transaction.accountCode || 'Conta não informada'}
                    </span>
                  </div>
                  <div className="dfc-category-modal-item-side">
                    <span className={`dfc-category-modal-item-amount ${flowType === 'outflow' ? 'negative' : 'positive'}`}>
                      {formatCurrency(transaction.amount)}
                    </span>
                    {navigation && <ArrowUpRight size={16} />}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default DFCCategoryTransactionsModal
